import { useState, useEffect } from 'react';
import { useCxRecentTestCalls, useCxTestCallInfo } from '@hooks/useCallExperience';
import { parseApiError } from '@utils/errorUtils';
import { formatDateTime } from '@utils/format';
import RecordingPlayer from '@components/common/RecordingPlayer';

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '–');
const seconds = (s) => (s == null ? '–' : `${Math.floor(s / 60)}m ${s % 60}s`);

function Row({ k, children }) {
  return <div className="cx-ci-row"><span className="cx-muted">{k}</span><span>{children}</span></div>;
}

function Section({ title, children }) {
  return <div className="cx-ci-section"><div className="cx-ci-title">{title}</div>{children}</div>;
}

function ContactSection({ contact }) {
  const timing = [
    ['Queue', contact.queueStartTime, contact.queueEndTime],
    ['Connected', contact.connectionStartTime, contact.connectionEndTime],
    ['Conference', contact.conferenceStartTime, contact.conferenceEndTime],
  ].filter(([, start, end]) => start || end);
  return (
    <Section title={`Contact record #${contact.contactId}`}>
      <Row k="Direction">{contact.direction || '–'} · {contact.category || '–'}</Row>
      <Row k="Answered / success">{yesNo(contact.answered)} / {yesNo(contact.success)}</Row>
      {(contact.patientName || contact.coachName) && <Row k="Patient / coach">{contact.patientName || '–'} / {contact.coachName || '–'}</Row>}
      <Row k="Disposition">{contact.dispositionReason || 'None yet'}{contact.transferReason && ` · transfer: ${contact.transferReason}`}</Row>
      {contact.dispositionNotes && <Row k="Disposition notes">{contact.dispositionNotes}</Row>}
      {timing.map(([label, start, end]) => (
        <Row key={label} k={label}>{formatDateTime(start)}{end ? ` → ${formatDateTime(end)}` : ' (ongoing)'}</Row>
      ))}
      {contact.notes && <pre className="cx-ci-notes">{contact.notes}</pre>}
      {contact.recordingSid
        ? <div className="cx-mt"><RecordingPlayer contactId={contact.contactId} recordingSid={contact.recordingSid} /></div>
        : <Row k="Recording">None</Row>}
    </Section>
  );
}

function IvrSection({ ivr }) {
  return (
    <Section title="IVR">
      <Row k="Status">{ivr.status || '–'}{ivr.answeredBy && ` · answered by ${ivr.answeredBy}`}</Row>
      {ivr.message && <Row k="Message">{ivr.message}</Row>}
      <Row k="Order">
        {ivr.orderGuid
          ? <>
            {ivr.orderUrl ? <a href={ivr.orderUrl} target="_blank" rel="noreferrer">{ivr.orderGuid}</a> : <code>{ivr.orderGuid}</code>}
            {ivr.orderStatus && <span className="cx-badge cx-badge-co cx-ml">{ivr.orderStatus}</span>}
          </>
          : 'No order created'}
      </Row>
      {ivr.answers.length > 0 && (
        <table className="cx-tc-table cx-small">
          <tbody>
            {ivr.answers.map((a, i) => (
              <tr key={`${a.timestamp}:${i}`}>
                <td className="cx-muted">{formatDateTime(a.timestamp)}</td>
                <td>{a.question}</td>
                <td><b>{a.value}</b>{a.convertedToOrder && <span className="cx-muted"> ✓ order</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function TwilioSection({ legs }) {
  if (legs.length === 0) return <Section title="Twilio"><span className="cx-muted cx-small">Twilio has no record of this call yet.</span></Section>;
  return (
    <Section title="Twilio">
      {legs.map((leg) => (
        <div key={leg.callSid} className="cx-ci-leg">
          <code className="cx-small">{leg.callSid}</code>
          <Row k="Status">{leg.status} · {leg.direction}</Row>
          <Row k="From → to">{leg.from || '–'} → {leg.to || '–'}</Row>
          <Row k="Duration / price">{seconds(leg.duration)} / {leg.price ? `$${leg.price}` : '–'}</Row>
        </div>
      ))}
    </Section>
  );
}

function ErrorsSection({ errors }) {
  const list = errors.data?.errors || [];
  return (
    <Section title={`Phone errors (${list.length})`}>
      {errors.isLoading && <span className="cx-muted cx-small">Loading…</span>}
      {list.length === 0 && !errors.isLoading && <span className="cx-muted cx-small">None logged.</span>}
      {list.map((e) => (
        <div key={e.phoneErrorId} className="cx-small cx-ci-err">
          <span className={`cx-badge ${e.severity === 'High' || e.severity === 'Critical' ? 'cx-badge-off' : 'cx-badge-warn'}`}>{e.severity}</span>
          {' '}{e.category}: {e.errorMessage}
          <span className="cx-muted"> · {formatDateTime(e.createdAt)}</span>
        </div>
      ))}
    </Section>
  );
}

/**
 * Call info for the Test call pane: pick one of the company's calls today (the call just placed is
 * picked automatically), then read its contact record, recording, Twilio legs, IVR answers and order,
 * and logged phone errors. Nothing refreshes on its own; "Get call info" re-reads everything.
 */
export default function TestCallInfo({ companyId, callSid }) {
  const [target, setTarget] = useState(callSid ? { callSid } : null);
  const recent = useCxRecentTestCalls(companyId);
  const { info, errors } = useCxTestCallInfo(companyId, target);

  // A call placed from the pane becomes the one shown.
  useEffect(() => { if (callSid) setTarget({ callSid }); }, [callSid]);

  const refresh = () => {
    recent.refetch();
    if (target) { info.refetch(); errors.refetch(); }
  };
  const selected = target?.contactId ? String(target.contactId) : '';
  const notFound = info.error?.response?.status === 404;

  return (
    <div className="cx-ci">
      <h4>Call info</h4>
      <div className="cx-tc-row">
        <select className="cx-ci-pick" value={selected} aria-label="Call to inspect"
          onChange={(e) => setTarget(e.target.value ? { contactId: Number(e.target.value) } : null)}>
          <option value="">{target?.callSid && !target.contactId ? 'The call just placed' : 'Pick one of today’s calls'}</option>
          {(recent.data || []).map((c) => (
            <option key={c.contactId} value={c.contactId}>
              {formatDateTime(c.timestamp)} · {c.isIvr ? 'IVR' : c.direction || 'call'}{c.patientName ? ` · ${c.patientName}` : ''}
            </option>
          ))}
        </select>
        <button type="button" className="cx-btn cx-btn-small" onClick={refresh} disabled={info.isFetching || recent.isFetching}>
          {info.isFetching ? 'Loading…' : '⟳ Get call info'}
        </button>
      </div>

      {!target && <p className="cx-hint">Place a call above, or pick a call to see its record.</p>}
      {notFound && <p className="cx-hint">Nothing recorded for this call yet. Try again in a few seconds.</p>}
      {info.error && !notFound && <div className="cx-error">{parseApiError(info.error)}</div>}

      {info.data && (
        <>
          {info.data.callSid && <Row k="Call SID"><code className="cx-small">{info.data.callSid}</code></Row>}
          {info.data.patientUrl && <Row k="Patient"><a href={info.data.patientUrl} target="_blank" rel="noreferrer">Open in Phoenix</a></Row>}
          {info.data.contact && <ContactSection contact={info.data.contact} />}
          {info.data.ivr && <IvrSection ivr={info.data.ivr} />}
          <TwilioSection legs={info.data.twilioLegs} />
          <ErrorsSection errors={errors} />
        </>
      )}
    </div>
  );
}
